import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { WorkerUniform } from '@/services/workersV2API';

type Props = {
  uniforms: WorkerUniform[];
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusColor = (
  status: string,
): 'theme' | 'danger' | 'warning' | 'neutral' => {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'theme';
    case 'rejected':
      return 'danger';
    case 'pending':
      return 'warning';
    default:
      return 'neutral';
  }
};

const UniformsCard = ({ uniforms }: Props) => {
  if (!uniforms || uniforms.length === 0) {
    return (
      <Card title="Uniforms (0)">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">No uniforms found</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card title={`Uniforms (${uniforms.length})`}>
      {uniforms.map((uniform) => (
        <Card.Section key={uniform.id}>
          <Stack gap={16} style={{ alignItems: 'flex-start' }}>
            {uniform.image_url && (
              <a
                href={uniform.image_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ flexShrink: 0 }}
              >
                <div
                  style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: '#F3F4F6',
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#1976d2';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <img
                    src={uniform.image_url}
                    alt={uniform.uniform_name || 'Uniform'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
              </a>
            )}
            <Stack vertical gap={12} style={{ flex: 1 }}>
              <Stack
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Body weight="semibold">
                  {uniform.uniform_name || 'Unknown Uniform'}
                </Body>
                <Badge
                  label={
                    uniform.status.charAt(0).toUpperCase() +
                    uniform.status.slice(1)
                  }
                  status={getStatusColor(uniform.status)}
                />
              </Stack>

              {uniform.comment && (
                <Stack
                  vertical
                  gap={4}
                  style={{
                    padding: '12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '6px',
                  }}
                >
                  <Small weight="semibold">Comment:</Small>
                  <Small>{uniform.comment}</Small>
                </Stack>
              )}

              <Stack gap={16}>
                <Small color="lighter">
                  Uploaded: {formatDate(uniform.created_at)}
                </Small>
                {uniform.created_at !== uniform.updated_at && (
                  <Small color="lighter">
                    Updated: {formatDate(uniform.updated_at)}
                  </Small>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Card.Section>
      ))}
    </Card>
  );
};

export default UniformsCard;
